/**
 * Customer Filtering Utilities
 * Shared filtering logic for customer lists
 */

/**
 * Filter customers by status
 * @param {Array} customers - Array of customer objects
 * @param {string} statusFilter - Status filter value
 * @returns {Array} Filtered customers
 */
export const filterByStatus = (customers, statusFilter) => {
  if (statusFilter === 'all') {
    return customers;
  }

  return customers.filter(customer => {
    const currentStatus = customer.acquisitionStatus;

    if (statusFilter === 'hot-opportunity') {
      return currentStatus === 'hot-opportunity';
    }

    if (statusFilter === 'appointment-set') {
      return currentStatus === 'appointment-set';
    }

    if (statusFilter === 'offer-sent') {
      return currentStatus === 'offer-sent';
    }

    if (statusFilter === 'not-interested') {
      return currentStatus === 'lost-declined';
    }

    return false;
  });
};

/**
 * Filter customers by search query
 * @param {Array} customers - Array of customer objects
 * @param {string} searchQuery - Search query string
 * @returns {Array} Filtered customers
 */
export const filterBySearchQuery = (customers, searchQuery) => {
  if (!searchQuery || searchQuery.trim() === '') {
    return customers;
  }

  const query = searchQuery.toLowerCase().trim();
  
  return customers.filter(c => {
    // Get all searchable fields (case-insensitive)
    const firstName = (c.firstName || '').toLowerCase();
    const lastName = (c.lastName || '').toLowerCase();
    const phone = (c.phone || c.cPhone || c.cellPhone || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const vehicleMake = (c.vehicleMake || '').toLowerCase();
    const vehicleModel = (c.vehicleModel || '').toLowerCase();
    const vehicleYear = (c.vehicleYear || '').toString().toLowerCase();
    
    // Also check full name (name field) and vehicle (vehicle field) for backward compatibility
    const name = (c.name || '').toLowerCase();
    const vehicle = (c.vehicle || '').toLowerCase();
    const vin = (c.vin || '').toLowerCase();
    
    // Check if query matches any of the searchable fields
    return firstName.includes(query) ||
           lastName.includes(query) ||
           name.includes(query) ||
           phone.includes(query) ||
           email.includes(query) ||
           vehicleMake.includes(query) ||
           vehicleModel.includes(query) ||
           vehicleYear.includes(query) ||
           vehicle.includes(query) ||
           vin.includes(query);
  });
};

/**
 * Apply all filters to customers
 * @param {Array} customers - Array of customer objects
 * @param {Object} filters - Filter object with statusFilter and searchQuery
 * @returns {Array} Filtered customers
 */
export const applyCustomerFilters = (customers, { statusFilter, searchQuery }) => {
  let filtered = customers;

  if (statusFilter && statusFilter !== 'all') {
    filtered = filterByStatus(filtered, statusFilter);
  }

  if (searchQuery) {
    filtered = filterBySearchQuery(filtered, searchQuery);
  }

  return filtered;
};

